import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import ClassIcon from '@mui/icons-material/Class';
import PeopleIcon from '@mui/icons-material/People';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  const [facultyName, setFacultyName] = useState('Professor');
  const [stats, setStats] = useState({
    activeClasses: 0,
    totalStudents: 0,
    registeredStudents: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Try multiple ways to get faculty ID
        let facultyId = localStorage.getItem('facultyId');
        
        // Check session storage if not in local storage
        if (!facultyId) {
          facultyId = sessionStorage.getItem('facultyId');
        }
        
        // Check JWT token if available
        if (!facultyId) {
          const token = localStorage.getItem('token') || sessionStorage.getItem('token');
          if (token) {
            try {
              // Try to decode the token (simple JWT structure, not secure for production)
              const base64Url = token.split('.')[1];
              const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
              const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
              }).join(''));
              
              const payload = JSON.parse(jsonPayload);
              facultyId = payload.facultyId || payload.faculty_id; // Try both formats
            } catch (e) {
              // Silent error handling - no console logs
            }
          }
        }

        if (!facultyId) {
          setLoading(false);
          return;
        }

        // Fix the API URL - remove the duplicate /api prefix
        const API_BASE_URL = process.env.REACT_APP_API_URL || '';
        
        // Fetch faculty info to get name
        try {
          const facultyURL = API_BASE_URL.includes('/api') 
            ? `${API_BASE_URL}/faculty/${facultyId}/` 
            : `${API_BASE_URL}/api/faculty/${facultyId}/`;
            
          const facultyResponse = await axios.get(facultyURL);
          const faculty = facultyResponse.data;
          setFacultyName(`${faculty.first_name}`);
        } catch (error) {
          // Silent error handling
        }

        // Fetch classes for the faculty
        const classesURL = API_BASE_URL.includes('/api') 
          ? `${API_BASE_URL}/classes/?faculty=${facultyId}` 
          : `${API_BASE_URL}/api/classes/?faculty=${facultyId}`;
          
        const classesResponse = await axios.get(classesURL);
        const classes = classesResponse.data;
        
        // Calculate total students and registered students
        let uniqueStudentIds = new Set();
        let uniqueRegisteredStudentIds = new Set();

        // Fetch class-student associations for each class
        for (const classItem of classes) {
          try {
            const studentsURL = API_BASE_URL.includes('/api') 
              ? `${API_BASE_URL}/class-students/?class_instance=${classItem.id}` 
              : `${API_BASE_URL}/api/class-students/?class_instance=${classItem.id}`;
              
            const classStudentsResponse = await axios.get(studentsURL);
            
            classStudentsResponse.data.forEach(association => {
              // Count all students (both pending and registered)
              if (association.student) {
                uniqueStudentIds.add(association.student);
                uniqueRegisteredStudentIds.add(association.student);
              } else if (association.pending_student) {
                uniqueStudentIds.add(association.pending_student);
              }
            });
          } catch (error) {
            // Silent error handling
          }
        }
        
        // Update stats state
        setStats({
          activeClasses: classes.length,
          totalStudents: uniqueStudentIds.size,
          registeredStudents: uniqueRegisteredStudentIds.size
        });
      } catch (error) {
        // Silent error handling
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Box>
      {/* Welcome Section */}
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 4,
          color: '#2C2C2C',
          fontWeight: 'bold'
        }}
      >
        Welcome Back, {facultyName}
      </Typography>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#FFFFFF',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
              },
            }}
          >
            <ClassIcon sx={{ fontSize: 40, color: '#DEA514', mr: 2 }} />
            <Box>
              <Typography variant="h6" color="text.secondary">
                Active Classes
              </Typography>
              {loading ? (
                <Skeleton variant="text" width={40} height={40} />
              ) : (
                <Typography variant="h4" sx={{ color: '#2C2C2C', fontWeight: 'bold' }}>
                  {stats.activeClasses}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#FFFFFF',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
              },
            }}
          >
            <PeopleIcon sx={{ fontSize: 40, color: '#DEA514', mr: 2 }} />
            <Box>
              <Typography variant="h6" color="text.secondary">
                Number of Students
              </Typography>
              {loading ? (
                <Skeleton variant="text" width={40} height={40} />
              ) : (
                <Typography variant="h4" sx={{ color: '#2C2C2C', fontWeight: 'bold' }}>
                  {stats.totalStudents}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#FFFFFF',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
              },
            }}
          >
            <HowToRegIcon sx={{ fontSize: 40, color: '#DEA514', mr: 2 }} />
            <Box>
              <Typography variant="h6" color="text.secondary">
                Registered Students
              </Typography>
              {loading ? (
                <Skeleton variant="text" width={40} height={40} />
              ) : (
                <Typography variant="h4" sx={{ color: '#2C2C2C', fontWeight: 'bold' }}>
                  {stats.registeredStudents}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Instructions Section */}
      <Paper
        sx={{
          p: 4,
          bgcolor: '#FFFFFF',
          mb: 4,
          borderLeft: '4px solid #DEA514',
        }}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 2,
            color: '#2C2C2C',
            fontWeight: 'bold'
          }}
        >
          Instructions
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#2C2C2C',
              fontWeight: 'bold',
              mb: 1
            }}
          >
            Choose a Role
          </Typography>
        </Box>
        
        <Box sx={{ ml: 2, mb: 3 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 'bold',
              color: '#2C2C2C',
              mb: 1
            }}
          >
            Role 1: I want to do a Student Event with QR sign-in's
          </Typography>
          
          <Box sx={{ ml: 2 }}>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                Go to Add Event — Create event – Add details and timings.
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                Download and Print QR code (To be displayed outside event location for students to sign in)
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                Copy event link from your event
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                Send email to faculty (with event link or event name) who have agreed to provide extra credit in their class.
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                Ask faculty to create their class on www.zipattend.com and ask their respective students to register on www.zipattend.com/auth/students with their phones
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                Faculty find your event and assign event to their respective classes. On day of event ask students to login on: www.zipattend.com/auth/students and scan event QR code using their phones.
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                Watch event attendance live and download if required.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ ml: 2, mb: 3 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 'bold',
              color: '#2C2C2C',
              mb: 1
            }}
          >
            Role 2: I want to provide extra credit to my students for attending events happening on campus
          </Typography>
          
          <Box sx={{ ml: 2 }}>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                Go to add class — Create class – Add students to class. If adding students using bulk import (CSV import)– follow instructions carefully about headers.
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                Click class to check student registrations (In student tab on top).
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                If students are not registered. Ask them to create an account on www.zipattend.com/auth/students on their phones.
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                Find event you want students to attend from event page.
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                Assign event to your class or multiple classes. Ask students in those classes to attend event for extra credit.
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                On day of event students have to login on: www.zipattend.com/auth/students (using their phones) and scan event QR code on location.
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <span style={{ marginRight: '8px', fontSize: '20px', lineHeight: '24px', flexShrink: 0 }}>•</span>
              <Typography variant="body1">
                After event ends: Click on your class → Click Event name. Download event attendance for your class.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Dashboard;